import { FLEET_BILLING_CYCLE_LABELS, type FleetAccountListItem } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Select,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Car } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { OpenAccountDialog } from "../../../features/fleet/account-dialog.js";
import {
  type FleetAccountFilters,
  fleetAccountsQuery,
  useFleetAccounts,
  useFleetPermissions,
} from "../../../features/fleet/queries.js";
import { AccountStatusBadge, FleetPage } from "../../../features/fleet/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatCount } from "../../../lib/format.js";

const searchSchema = z.object({
  q: z.string().max(100).optional().catch(undefined),
  status: z.enum(["ACTIVE", "SUSPENDED", "UNCONFIGURED"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/filo/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: FleetAccountsRoute,
});

/**
 * Filo hesapları (Prompt 17 web): `FLEET_CUSTOMER` rollü HER cari listelenir; filo ayarı açılmamış olanlar "Filo ayarı
 * yok" rozetiyle görünür ve buradan açılır (fatura dönemi, portal). Kredi limiti CARİNİNDİR (cari kartından yönetilir).
 */
function FleetAccountsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <FleetPage title={t("menu.fleetAccounts")} description={t("fleet.accounts.description")}>
      <AccountsBody />
    </FleetPage>
  );
}

function AccountsBody(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useFleetPermissions();
  const [searchText, setSearchText] = useState(search.q ?? "");
  const [opening, setOpening] = useState<FleetAccountListItem | null>(null);

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== searchText.trim()) setSearch({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const filters: FleetAccountFilters = { q: search.q, status: search.status };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useFleetAccounts(filters, pages.cursor);

  const columns = useMemo<DataTableColumn<FleetAccountListItem>[]>(
    () => [
      {
        id: "counterparty",
        header: t("fleet.fields.counterparty"),
        hideable: false,
        exportValue: (row) => `${row.counterpartyCode} ${row.counterpartyName}`,
        cell: (row) => (
          <Link
            to="/filo/$counterpartyId"
            params={{ counterpartyId: row.counterpartyId }}
            className="grid gap-0.5 underline-offset-4 hover:underline"
          >
            <span className="font-medium text-primary">{row.counterpartyName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.counterpartyCode}</span>
          </Link>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => t(`fleet.accountStatus.${row.account?.status ?? "UNCONFIGURED"}`),
        cell: (row) => (
          <span className="flex flex-wrap gap-1">
            <AccountStatusBadge status={row.account?.status ?? null} />
            {row.counterpartyBlocked ? (
              <StatusBadge tone="danger">{t("fleet.accounts.blocked")}</StatusBadge>
            ) : null}
            {row.counterpartyActive ? null : <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>}
          </span>
        ),
      },
      {
        id: "billingCycle",
        header: t("fleet.fields.billingCycle"),
        exportValue: (row) => (row.account ? FLEET_BILLING_CYCLE_LABELS[row.account.billingCycle] : null),
        cell: (row) =>
          row.account ? (
            FLEET_BILLING_CYCLE_LABELS[row.account.billingCycle]
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "portal",
        header: t("fleet.fields.portal"),
        exportValue: (row) =>
          row.account ? (row.account.portalEnabled ? t("common.on") : t("common.off")) : null,
        cell: (row) =>
          row.account ? (
            <StatusBadge tone={row.account.portalEnabled ? "info" : "neutral"}>
              {row.account.portalEnabled ? t("common.on") : t("common.off")}
            </StatusBadge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "vehicles",
        header: t("fleet.fields.activeVehicles"),
        align: "right",
        exportValue: (row) => row.activeVehicleCount,
        cell: (row) => <span className="tabular">{formatCount(row.activeVehicleCount)}</span>,
      },
      {
        id: "actions",
        header: t("common.actions"),
        exportValue: () => null,
        cell: (row) =>
          row.account === null && permissions.canManage ? (
            <Button variant="outline" size="sm" onClick={() => setOpening(row)}>
              {t("fleet.accounts.open")}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" asChild>
              <Link to="/filo/$counterpartyId" params={{ counterpartyId: row.counterpartyId }}>
                {t("fleet.accounts.details")}
                <span className="sr-only"> — {row.counterpartyName}</span>
              </Link>
            </Button>
          ),
      },
    ],
    [t, permissions.canManage],
  );
  const hasFilters = Boolean(search.q || search.status);

  return (
    <>
      <DataTable
        caption={t("menu.fleetAccounts")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.counterpartyId}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Car />}
            title={hasFilters ? t("fleet.accounts.emptyFiltered") : t("fleet.accounts.emptyTitle")}
            description={t("fleet.accounts.emptyDescription")}
            action={
              <Link to="/cari" className="text-sm underline underline-offset-4">
                {t("menu.counterparties")}
              </Link>
            }
          />
        }
        filters={
          <>
            <FormField label={t("common.search")} className="w-60">
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder={t("fleet.accounts.searchPlaceholder")}
                />
              )}
            </FormField>
            <FormField label={t("common.status")} className="w-48">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) => setSearch({ status: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {(["ACTIVE", "SUSPENDED", "UNCONFIGURED"] as const).map((status) => (
                    <option key={status} value={status}>
                      {t(`fleet.accountStatus.${status}`)}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<FleetAccountListItem>(
            async (cursor) =>
              (
                await api.GET("/fleet/accounts", {
                  params: { query: fleetAccountsQuery(filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "filo-hesaplari",
            sheetName: t("menu.fleetAccounts"),
            columns: visible,
            rows,
          });
        }}
      />
      <OpenAccountDialog
        item={opening}
        onClose={() => setOpening(null)}
        onOpened={(counterpartyId) =>
          void navigate({ to: "/filo/$counterpartyId", params: { counterpartyId } })
        }
      />
    </>
  );
}
